import React, { useRef } from 'react';

import type { SitrepSectionType } from 'components/DisasterGPT/hooks/useSitrepsV2';
import { SharedIcon } from 'components/SharedIcon/SharedIcon';

type SitrepSectionTabsProps = {
  sections: SitrepSectionType[];
  /**
   * Use activeSectionIndex from useSitrepSectionSelector for correct active
   * highlighting.
   */
  activeSectionIndex: number | null;
  onSectionChange: (index: number) => void;
  className?: string;
  idBase?: string;
};

/**
 * Renders a horizontal row of tab buttons for navigating sitrep sections.
 * AI-generated sections are indicated with a sparkle icon. Returns null when
 * there are no sections.
 */
const SitrepSectionTabs: React.FC<SitrepSectionTabsProps> = ({
  sections,
  activeSectionIndex,
  onSectionChange,
  className,
  idBase,
}) => {
  const tabRefs = useRef<Array<HTMLButtonElement | null>>([]);

  if (sections.length === 0) return null;

  const selectedIndex = activeSectionIndex ?? 0;

  const focusSection = (index: number) => {
    onSectionChange(index);
    tabRefs.current[index]?.focus();
  };

  const handleKeyDown = (
    event: React.KeyboardEvent<HTMLButtonElement>,
    index: number
  ) => {
    let nextIndex: number | null = null;

    switch (event.key) {
      case 'ArrowRight':
        nextIndex = index === sections.length - 1 ? 0 : index + 1;
        break;
      case 'ArrowLeft':
        nextIndex = index === 0 ? sections.length - 1 : index - 1;
        break;
      case 'Home':
        nextIndex = 0;
        break;
      case 'End':
        nextIndex = sections.length - 1;
        break;
      default:
        return;
    }

    event.preventDefault();
    focusSection(nextIndex);
  };

  return (
    <div
      className={`d-flex overflow-auto p-1 ${className ? ` ${className}` : ''}`}
      role="tablist"
      aria-label="SITREP sections"
      aria-orientation="horizontal"
    >
      {sections.map((section, index) => (
        <button
          key={section.id}
          type="button"
          id={idBase ? `${idBase}-tab-${index}` : undefined}
          role="tab"
          aria-controls={idBase ? `${idBase}-panel-${index}` : undefined}
          aria-selected={selectedIndex === index}
          tabIndex={selectedIndex === index ? 0 : -1}
          className={`reports-card__section-tab ${selectedIndex === index ? ' active' : ''}`}
          onClick={() => onSectionChange(index)}
          onKeyDown={(event) => handleKeyDown(event, index)}
          ref={(element) => {
            tabRefs.current[index] = element;
          }}
        >
          <span className="d-flex align-items-center gap-1">
            {section.name}
            {section.type === 'ai' && (
              <SharedIcon
                iconName="auto_awesome"
                size="sm"
                aria-hidden="true"
              />
            )}
          </span>
        </button>
      ))}
    </div>
  );
};

export default SitrepSectionTabs;
